import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';
import reverse from 'lodash/reverse';
import {
  PortfolioDTO,
  PortfolioRiskNameDTO,
  useGetWorkflowResultsQuery,
} from './api/useGetWorkflowResultsQuery';
import { AsyncComponentHookResult } from '../../Types/ComponentHookResult';
import { AssetAllocation, getColorByColorGroup } from '../../CommonUtils/getAssetColors';

interface Portfolio extends Omit<PortfolioDTO, 'assetAllocation'> {
  assetAllocation: AssetAllocation[];
}

interface UseAutoAccountWorkflowResultData {
  tabNameList: PortfolioRiskNameDTO[];
  tabContent: Portfolio;
  tabIndex: number | null;
}

interface UseAutoAccountWorkflowResultActions {
  setTabIndex: Dispatch<SetStateAction<number | null>>;
}

export const useAutoAccountWorkflowResult = (): AsyncComponentHookResult<
  UseAutoAccountWorkflowResultData,
  UseAutoAccountWorkflowResultActions
> => {
  const queryInfo = useGetWorkflowResultsQuery();

  const [tabIndex, setTabIndex] = useState<number | null>(null);

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    const tabNameList = queryInfo.data.map(({ name }) => name) as PortfolioRiskNameDTO[];

    const selectedTabIndex =
      tabIndex === null
        ? queryInfo.data.findIndex(({ name }) => name === PortfolioRiskNameDTO.Recommended)
        : tabIndex;

    const tabContent = {
      ...queryInfo.data[selectedTabIndex],
      assetAllocation: reverse(
        sortBy(
          getColorByColorGroup(queryInfo.data[selectedTabIndex].assetAllocation),
          ({ y }) => y,
        ),
      ),
    };

    setTabIndex(selectedTabIndex);

    return { tabNameList, tabContent, tabIndex };
  }, [queryInfo.data, tabIndex]);

  return {
    ...queryInfo,
    data,
    actions: {
      setTabIndex,
    },
  };
};
